import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { ArrowLeft, Bitcoin, Coins, Lock, Wallet, Loader2, CheckCircle2, XCircle, Clock, AlertTriangle } from "lucide-react";

const fmtSats = (s) => (s == null ? "—" : `${s.toLocaleString()} sat`);
const fmtBtc = (s) => (s == null ? "—" : `₿ ${(s / 100_000_000).toFixed(8)}`);
const fmtTime = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

const STATUS_BADGE = {
    submitting: { label: "Submitting", c: "bg-vibrantyellow", icon: Loader2 },
    processing: { label: "Processing", c: "bg-vibrantblue text-white", icon: Loader2 },
    pending_admin: { label: "Pending approval", c: "bg-vibrantorange text-white", icon: Clock },
    confirmed: { label: "Sent", c: "bg-emerald-500 text-white", icon: CheckCircle2 },
    failed: { label: "Failed", c: "bg-vibrantpink text-white", icon: XCircle },
    rejected: { label: "Rejected", c: "bg-vibrantpink text-white", icon: XCircle },
};

export default function Cashout() {
    const { user, loading: authLoading, refresh } = useAuth();
    const [credits, setCredits] = useState(0);
    const [amount, setAmount] = useState(50);
    const [channel, setChannel] = useState("onchain");
    const [addr, setAddr] = useState("");
    const [invoice, setInvoice] = useState("");
    const [quote, setQuote] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState("");
    const [success, setSuccess] = useState(null);
    const [history, setHistory] = useState([]);

    const isLoggedIn = user && user !== false;

    const loadBalance = useCallback(async () => {
        if (!isLoggedIn) return;
        try {
            const { data } = await api.get("/payments/i2v/balance");
            setCredits(data.credits);
        } catch { /* ignore */ }
    }, [isLoggedIn]);

    const loadHistory = useCallback(async () => {
        if (!isLoggedIn) return;
        try {
            const { data } = await api.get("/cashout/history");
            setHistory(data);
        } catch { /* ignore */ }
    }, [isLoggedIn]);

    const loadQuote = useCallback(async (a, ch) => {
        if (!a || a < 1) { setQuote(null); return; }
        try {
            const { data } = await api.get(`/cashout/quote?amount_credits=${a}&channel=${ch}`);
            setQuote(data);
        } catch { setQuote(null); }
    }, []);

    useEffect(() => { loadBalance(); loadHistory(); }, [loadBalance, loadHistory]);
    useEffect(() => { loadQuote(amount, channel); }, [amount, channel, loadQuote]);
    useEffect(() => {
        const t = setInterval(loadHistory, 10000);
        return () => clearInterval(t);
    }, [loadHistory]);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setSuccess(null);
        if (channel === "onchain" && !addr.trim()) { setErr("Enter your Bitcoin address"); return; }
        if (channel === "lightning" && !invoice.trim()) { setErr("Paste a Lightning invoice"); return; }
        if (amount < 1 || amount > credits) { setErr("Invalid amount"); return; }
        setSubmitting(true);
        try {
            const body = { amount_credits: parseInt(amount, 10), channel };
            if (channel === "onchain") body.btc_address = addr.trim();
            else body.ln_invoice = invoice.trim();
            const { data } = await api.post("/cashout/request", body);
            setSuccess(data);
            setAddr("");
            setInvoice("");
            loadBalance();
            loadHistory();
            if (refresh) refresh();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            setErr(typeof detail === "string" ? detail : "Couldn't submit cashout");
        } finally {
            setSubmitting(false);
        }
    };

    if (authLoading) return <div className="p-10 font-heading">Loading…</div>;

    if (!isLoggedIn) {
        return (
            <div className="max-w-2xl mx-auto p-8">
                <div data-testid="cashout-login-gate" className="brutal-card p-8 bg-white">
                    <div className="w-12 h-12 bg-vibrantpink border-[3px] border-ink rounded-2xl flex items-center justify-center shadow-brutal mb-4">
                        <Lock className="w-6 h-6 text-white" strokeWidth={2.5} />
                    </div>
                    <h2 className="font-heading font-black text-3xl text-ink tracking-tighter mb-2">Log in to cash out</h2>
                    <div className="flex gap-3">
                        <Link to="/login" className="brutal-btn-primary">Log in</Link>
                        <Link to="/register" className="brutal-btn-secondary">Create account</Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <Link to="/" className="inline-flex items-center gap-1.5 font-heading font-bold text-xs uppercase tracking-widest text-ink/60 hover:text-vibrantorange transition-colors mb-3" data-testid="cashout-back">
                <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.5} /> Back to Endfb
            </Link>

            <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-vibrantorange border-[3px] border-ink rounded-2xl flex items-center justify-center shadow-brutal-lg">
                    <Bitcoin className="w-6 h-6 text-white" strokeWidth={2.5} />
                </div>
                <div className="flex-1">
                    <h1 className="font-heading font-black text-3xl sm:text-5xl tracking-tighter text-ink leading-tight">
                        Cash out to <span className="text-vibrantorange">Bitcoin</span>
                    </h1>
                    <p className="font-body text-ink/70 text-sm">1 credit = $0.40. Min payout 0.0001 BTC. Network + 1% on-chain fee is deducted.</p>
                </div>
                <div className="hidden sm:flex items-center gap-1.5 bg-vibrantyellow border-[3px] border-ink rounded-xl px-3 py-2 shadow-brutal">
                    <Coins className="w-4 h-4 text-ink" strokeWidth={2.5} />
                    <span className="font-heading font-bold text-sm text-ink" data-testid="cashout-credits">{credits}</span>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                {/* Form */}
                <form onSubmit={submit} className="lg:col-span-3 brutal-card p-5 bg-white space-y-4" data-testid="cashout-form">
                    <div>
                        <label className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink/60 block mb-1">Amount (credits)</label>
                        <input
                            type="number"
                            data-testid="cashout-amount"
                            min={1}
                            max={credits}
                            value={amount}
                            onChange={(e) => setAmount(Math.max(0, parseInt(e.target.value || "0", 10)))}
                            className="brutal-input"
                        />
                        <div className="flex gap-2 mt-2">
                            {[25, 50, 100, 500].map((v) => (
                                <button key={v} type="button" onClick={() => setAmount(Math.min(v, credits))}
                                        className="brutal-btn-white text-xs py-1.5 flex-1">{v}</button>
                            ))}
                            <button type="button" onClick={() => setAmount(credits)}
                                    className="brutal-btn-white text-xs py-1.5 flex-1">max</button>
                        </div>
                    </div>

                    <div>
                        <label className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink/60 block mb-1">Payout method</label>
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                type="button"
                                onClick={() => setChannel("onchain")}
                                data-testid="cashout-channel-onchain"
                                className={`brutal-btn py-2 text-sm ${channel === "onchain" ? "bg-ink text-white" : "bg-white text-ink"}`}
                            >
                                <Bitcoin className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.5} /> On-chain
                                <span className="block text-[9px] font-body opacity-70">~10 min · 1% fee</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setChannel("lightning")}
                                data-testid="cashout-channel-lightning"
                                className={`brutal-btn py-2 text-sm ${channel === "lightning" ? "bg-vibrantorange text-white" : "bg-white text-ink"}`}
                            >
                                ⚡ Lightning
                                <span className="block text-[9px] font-body opacity-70">instant · 0% fee</span>
                            </button>
                        </div>
                    </div>

                    {channel === "onchain" ? (
                        <div>
                            <label className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink/60 block mb-1">Your Bitcoin address (mainnet)</label>
                            <input
                                type="text"
                                data-testid="cashout-address"
                                value={addr}
                                onChange={(e) => setAddr(e.target.value)}
                                placeholder="bc1… or 3… or 1…"
                                className="brutal-input font-mono text-sm"
                            />
                        </div>
                    ) : (
                        <div>
                            <label className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink/60 block mb-1">Lightning invoice (lnbc…)</label>
                            <textarea
                                data-testid="cashout-invoice"
                                value={invoice}
                                onChange={(e) => setInvoice(e.target.value)}
                                placeholder="lnbc..."
                                rows={3}
                                className="brutal-input font-mono text-xs resize-none"
                            />
                            <p className="font-body text-[10px] text-ink/50 mt-1">
                                Generate the invoice in your wallet for the receive amount shown below.
                            </p>
                        </div>
                    )}

                    {quote && (
                        <div data-testid="cashout-quote" className="brutal-card p-3 bg-cream">
                            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
                                <span className="font-body text-ink/60">USD value</span>
                                <span className="font-heading font-bold text-ink text-right">${quote.amount_usd}</span>
                                <span className="font-body text-ink/60">BTC price</span>
                                <span className="font-heading font-bold text-ink text-right">${Math.round(quote.btc_price_usd).toLocaleString()}</span>
                                <span className="font-body text-ink/60">You receive</span>
                                <span className="font-heading font-black text-vibrantorange text-right">{fmtBtc(quote.net_sats)}</span>
                                <span className="font-body text-ink/60">In sats</span>
                                <span className="font-heading font-bold text-ink text-right tabular-nums">{fmtSats(quote.net_sats)}</span>
                            </div>
                            {quote.below_min && (
                                <p data-testid="cashout-below-min" className="mt-2 font-body text-xs text-vibrantpink font-semibold">
                                    Below minimum payout (0.0001 BTC). Increase the amount.
                                </p>
                            )}
                            {quote.needs_admin_approval && !quote.below_min && (
                                <div className="mt-2 flex items-start gap-1.5">
                                    <AlertTriangle className="w-3.5 h-3.5 text-vibrantorange flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                                    <p data-testid="cashout-needs-approval" className="font-body text-xs text-ink/70">
                                        Over 500 credits — your request will be queued for admin approval (usually within 24h).
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {err && <p data-testid="cashout-error" className="font-body text-sm text-vibrantpink font-semibold">{err}</p>}
                    {success && (
                        <div data-testid="cashout-success" className="brutal-card p-3 bg-emerald-100">
                            <p className="font-heading font-bold text-sm text-emerald-800">
                                {success.status === "pending_admin"
                                    ? "Submitted for admin approval ✓"
                                    : "Withdrawal submitted to OpenNode ✓"}
                            </p>
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={submitting || (channel === "onchain" ? !addr.trim() : !invoice.trim()) || amount < 1 || amount > credits || (quote && quote.below_min)}
                        className="brutal-btn-primary w-full"
                        data-testid="cashout-submit"
                    >
                        {submitting ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" strokeWidth={2.5} /> Submitting…</> : <><Wallet className="w-4 h-4 mr-1.5" strokeWidth={2.5} /> Cash out</>}
                    </button>
                </form>

                {/* History */}
                <div className="lg:col-span-2 brutal-card p-5 bg-white" data-testid="cashout-history">
                    <h3 className="font-heading font-black text-lg text-ink tracking-tight mb-3">Your cashouts</h3>
                    {history.length === 0 ? (
                        <p className="font-body text-sm text-ink/50">No cashouts yet.</p>
                    ) : (
                        <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                            {history.map((h) => {
                                const b = STATUS_BADGE[h.status] || STATUS_BADGE.processing;
                                const Icon = b.icon;
                                return (
                                    <div key={h.id} className="border-2 border-ink rounded-xl p-2.5 bg-cream">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className={`inline-flex items-center gap-1 font-heading font-bold text-[10px] uppercase tracking-widest border-2 border-ink rounded-md px-1.5 py-0.5 ${b.c}`}>
                                                <Icon className="w-3 h-3" strokeWidth={3} /> {b.label}
                                            </span>
                                            <span className="font-heading font-black text-ink text-sm">
                                                {h.channel === "lightning" ? "⚡ " : ""}{h.amount_credits} cr
                                            </span>
                                        </div>
                                        <div className="font-body text-[11px] text-ink/60 truncate" title={h.btc_address || h.ln_invoice}>
                                            {h.btc_address || (h.ln_invoice ? `${h.ln_invoice.slice(0, 50)}…` : "—")}
                                        </div>
                                        <div className="flex justify-between font-body text-[11px] text-ink/60 mt-1">
                                            <span>{fmtBtc(h.net_sats)}</span>
                                            <span>{fmtTime(h.created_at)}</span>
                                        </div>
                                        {h.opennode_error && (
                                            <div className="font-body text-[11px] text-vibrantpink mt-1">{String(h.opennode_error).slice(0, 120)}</div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
